import { supabase } from '@/lib/supabase';
import { Supplier, SupplierInsert, SupplierUpdate, PaginatedResult, Purchase, SupplierReturn } from '@/types/models';
import { PAGE_SIZE } from '@/lib/constants';

export interface SupplierLedgerEntry {
  id: string;
  date: string;
  type: 'purchase' | 'payment' | 'supplier_return' | 'cash_refund';
  reference: string;
  referenceId: string;
  description: string;
  debit: number;   // Payments made to supplier or return credits applied
  credit: number;  // Purchases received (creating liability) or refunds received
  runningBalance: number;
}

export interface SupplierLedgerSummary {
  totalPurchases: number;
  totalPaid: number;
  totalSupplierReturns: number;
  outstandingPayable: number;
  purchasesCount: number;
  entries: SupplierLedgerEntry[];
}

/**
 * Fetch a paginated, searchable list of suppliers from public.suppliers.
 * Owner/manager only — enforced at route level via RoleGuard.
 */
export async function fetchSuppliers(params: {
  search?: string;
  page?: number;
}): Promise<PaginatedResult<Supplier>> {
  const { search = '', page = 0 } = params;
  const from = page * PAGE_SIZE;
  const to = from + PAGE_SIZE - 1;

  let query = supabase
    .from('suppliers')
    .select('*', { count: 'exact' })
    .eq('is_active', true)
    .order('name', { ascending: true })
    .order('id', { ascending: true })
    .range(from, to);

  if (search.trim()) {
    query = query.or(
      `name.ilike.%${search.trim()}%,contact_name.ilike.%${search.trim()}%,phone.ilike.%${search.trim()}%,email.ilike.%${search.trim()}%`
    );
  }

  const { data, error, count } = await query;

  if (error) throw error;

  return { data: (data ?? []) as Supplier[], count: count ?? 0 };
}

/**
 * Fetch a single supplier by id from public.suppliers.
 */
export async function fetchSupplierById(id: string): Promise<Supplier> {
  const { data, error } = await supabase
    .from('suppliers')
    .select('*')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as Supplier;
}

/**
 * Insert a new supplier row into public.suppliers.
 */
export async function createSupplier(payload: SupplierInsert): Promise<Supplier> {
  const existing = await fetchAllActiveSuppliers();
  const sameName = existing.find(s => supplierNameKey(s.name) === supplierNameKey(payload.name));
  if (sameName) throw new Error(`Supplier "${sameName.name}" already exists${sameName.phone ? ` (${sameName.phone})` : ''}. Select the existing supplier instead of creating another.`);
  const { data, error } = await supabase
    .from('suppliers')
    .insert(payload)
    .select()
    .single();

  if (error) throw error;
  return data as Supplier;
}

/**
 * Update an existing supplier in public.suppliers.
 */
export async function updateSupplier(id: string, payload: SupplierUpdate): Promise<Supplier> {
  const { data, error } = await supabase
    .from('suppliers')
    .update(payload)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Supplier;
}

/**
 * Fetch authoritative Supplier Account Ledger with chronological timeline & summary metrics.
 * Owner / Manager Only.
 */
export async function fetchSupplierLedger(supplierId: string): Promise<SupplierLedgerSummary> {
  // 1. Fetch purchases
  const { data: purchData, error: purchErr } = await supabase
    .from('purchases')
    .select('*')
    .eq('supplier_id', supplierId)
    .neq('status', 'cancelled')
    .order('purchase_date', { ascending: true });

  if (purchErr) throw purchErr;
  const purchases = (purchData ?? []) as Purchase[];

  // 2. Fetch payments linked to those purchases
  const purchIds = purchases.map((p) => p.id);
  let payments: any[] = [];
  if (purchIds.length > 0) {
    const { data: payData, error: payErr } = await supabase
      .from('payments')
      .select('*')
      .in('purchase_id', purchIds)
      .eq('direction', 'out')
      .order('payment_date', { ascending: true });

    if (!payErr && payData) {
      payments = payData;
    }
  }

  // 3. Fetch supplier returns
  const { data: returnsData, error: retErr } = await supabase
    .from('supplier_returns')
    .select('*')
    .eq('supplier_id', supplierId)
    .eq('status', 'completed')
    .order('return_date', { ascending: true });

  if (retErr) throw retErr;
  const supplierReturns = (returnsData ?? []) as SupplierReturn[];

  // Build Chronological Events
  const rawEvents: Array<{
    date: string;
    type: 'purchase' | 'payment' | 'supplier_return' | 'cash_refund';
    reference: string;
    referenceId: string;
    description: string;
    debit: number;
    credit: number;
  }> = [];

  for (const p of purchases) {
    rawEvents.push({
      date: p.purchase_date,
      type: 'purchase',
      reference: p.purchase_number,
      referenceId: p.id,
      description: `Purchase #${p.purchase_number}${p.supplier_invoice_number ? ' (Inv: ' + p.supplier_invoice_number + ')' : ''}`,
      debit: 0,
      credit: Number(p.total) || 0,
    });
  }

  for (const pay of payments) {
    rawEvents.push({
      date: pay.payment_date || pay.created_at,
      type: 'payment',
      reference: pay.reference || 'Payment',
      referenceId: pay.purchase_id || pay.id,
      description: `Payment to supplier (${pay.payment_method || 'Cash'})${pay.reference ? ' - ' + pay.reference : ''}`,
      debit: Number(pay.amount) || 0,
      credit: 0,
    });
  }

  for (const sr of supplierReturns) {
    const retVal = Number(sr.return_value) || 0;

    const cashRefund = Number(sr.refund_amount) || 0;

    if (retVal > 0) {
      rawEvents.push({
        date: sr.return_date,
        type: 'supplier_return',
        reference: sr.return_number,
        referenceId: sr.id,
        description: `Supplier Return #${sr.return_number}${sr.reason ? ' (' + sr.reason + ')' : ''}`,
        debit: retVal,
        credit: 0,
      });
    }

    if (cashRefund > 0) {
      rawEvents.push({
        date: sr.return_date,
        type: 'cash_refund',
        reference: sr.return_number,
        referenceId: sr.id,
        description: `Supplier Cash Refund received #${sr.return_number}`,
        debit: 0,
        credit: cashRefund,
      });
    }
  }

  // Sort chronological
  rawEvents.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  // Running balance (liability / payable)
  let running = 0;
  const entries: SupplierLedgerEntry[] = rawEvents.map((ev, idx) => {
    running += (ev.credit - ev.debit);
    return {
      id: `${ev.type}-${ev.referenceId}-${idx}`,
      ...ev,
      runningBalance: Math.max(0, running),
    };
  });

  const totalPurchases = purchases.reduce((acc, p) => acc + (Number(p.total) || 0), 0);
  const totalPaid = payments.reduce((acc, pay) => acc + (Number(pay.amount) || 0), 0);
  const totalSupplierReturns = supplierReturns.reduce((acc, sr) => acc + (Number(sr.return_value) || 0), 0);
  const totalCreditsApplied = supplierReturns.reduce((acc, sr) => acc + (Number(sr.credit_applied_to_payable) || 0), 0);
  const outstandingPayable = Math.max(0, totalPurchases - totalPaid - totalCreditsApplied);

  return {
    totalPurchases,
    totalPaid,
    totalSupplierReturns,
    outstandingPayable,
    purchasesCount: purchases.length,
    entries,
  };
}

/**
 * Fetch all active suppliers with bounded pagination (1000 rows/page).
 * Used for deterministic matching in spreadsheet imports and purchase forms.
 */
export async function fetchAllActiveSuppliers(): Promise<Supplier[]> {
  const PAGE_SIZE = 1000;
  const all: Supplier[] = [];
  let from = 0;

  for (;;) {
    const { data, error } = await supabase
      .from('suppliers')
      .select('*')
      .eq('is_active', true)
      .order('name', { ascending: true })
      .order('id', { ascending: true })
      .range(from, from + PAGE_SIZE - 1);

    if (error) throw error;
    const page = (data ?? []) as Supplier[];
    all.push(...page);
    if (page.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  return all;
}

export function supplierNameKey(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

export interface SupplierMergePreview {
  primary_id: string;
  suppliers: Supplier[];
  counts: { purchases: number; purchase_items: number; payments: number; laptops: number; accessories: number; supplier_returns: number };
  combined_opening_balance: number;
  preview_token: string;
}

export async function previewSupplierMerge(primaryId: string, duplicateIds: string[]): Promise<SupplierMergePreview> {
  const { data, error } = await supabase.rpc('preview_supplier_merge', { p_primary_id: primaryId, p_duplicate_ids: duplicateIds });
  if (error) throw error;
  return data as SupplierMergePreview;
}

export async function mergeSuppliers(primaryId: string, duplicateIds: string[], previewToken: string, reason: string): Promise<void> {
  const { error } = await supabase.rpc('merge_suppliers', {
    p_primary_id: primaryId, p_duplicate_ids: duplicateIds, p_preview_token: previewToken, p_reason: reason,
  });
  if (error) throw error;
}

export function groupDuplicateSuppliers(suppliers: Supplier[]): Supplier[][] {
  const groups = new Map<string, Supplier[]>();
  for (const supplier of suppliers) {
    const key = supplierNameKey(supplier.name);
    groups.set(key, [...(groups.get(key) || []), supplier]);
  }
  return [...groups.values()].filter(group => group.length > 1);
}
